/**
 * Addresses and areas: list, create, update, delete, set default.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAddresses,
  fetchAreas,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  type AddressPayload,
  type Address,
  type Area,
} from './api';
import { useAuthStore } from '../../../shared/auth/store';
import { resolveQueryRetry } from '../../../data/query/queryRetry';
import { addressesQueryKey, areasQueryKey } from './keys';

export type { Address, Area } from './api';

export interface AddressStub {
  id?: number;
  is_default?: boolean;
  [key: string]: unknown;
}

export function useAddresses() {
  const customer_id = useAuthStore(s => s.user?.id);
  const query = useQuery({
    queryKey: [...addressesQueryKey, customer_id],
    queryFn: () => fetchAddresses(customer_id!),
    staleTime: 1000 * 60 * 2,
    enabled: typeof customer_id === 'number',
  });
  const data = query.data ?? [];
  return {
    data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}

export function useAreas(): { data: any[]; isLoading: boolean; error: Error | null; refetch: () => void; isRefetching: boolean } {
  const query = useQuery({
    queryKey: areasQueryKey,
    queryFn: fetchAreas,
    staleTime: 1000 * 60 * 10,
    enabled: true,
    refetchOnMount: true,
    retry: (failure_count, error) => resolveQueryRetry(failure_count, error, 2),
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
  const data = Array.isArray(query.data) ? query.data : [];
  return {
    data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useCreateAddress() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: AddressPayload) => createAddress(payload),
    onSuccess: () => client.invalidateQueries({ queryKey: addressesQueryKey }),
  });
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateAddress() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ addressId, customerId, data }: { addressId: number; customerId: number; data: Partial<AddressPayload> }) =>
      updateAddress(addressId, customerId, data),
    onSuccess: () => client.invalidateQueries({ queryKey: addressesQueryKey }),
  });
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useDeleteAddress() {
  const client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);
  const mutation = useMutation({
    mutationFn: (addressId: number) => deleteAddress(addressId, customer_id ?? undefined),
    onSuccess: () => client.invalidateQueries({ queryKey: addressesQueryKey }),
  });
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useSetDefaultAddress() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: { customerId: number; addressId: number } | number) =>
      typeof payload === 'number' ? setDefaultAddress(payload) : setDefaultAddress(payload.addressId, payload.customerId),
    onSuccess: () => client.invalidateQueries({ queryKey: addressesQueryKey }),
  });
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
